import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Users, CreditCard, Key, ClipboardList, Home, LogOut, ChevronDown, Building, BookOpen, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ThemeToggle } from '../../components/ThemeToggle';

const adminNavItems = [
  { id: 'overview', name: 'Overview', icon: Home, path: '/admin' },
  { id: 'blog', name: 'Blog Management', icon: BookOpen, path: '/admin/blog' },
  { id: 'credits', name: 'Credit Management', icon: CreditCard, path: '/admin/credits' },
  { id: 'users', name: 'User Management', icon: Users, path: '/admin/users' },
  { id: 'invites', name: 'Invitation Codes', icon: Key, path: '/admin/invites' },
  { id: 'logs', name: 'Admin Logs', icon: ClipboardList, path: '/admin/logs' },
];

export function AdminDashboard() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!user || !isAdmin()) {
      navigate('/', { replace: true });
    }
  }, [user, isAdmin, navigate]);

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close menu when route changes
  useEffect(() => {
    return () => setShowUserMenu(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const handleClickOutside = (event: React.MouseEvent) => {
    const sidebar = document.getElementById('admin-sidebar');
    const toggleButton = document.getElementById('sidebar-toggle');
    
    if (
      isOpen &&
      sidebar &&
      !sidebar.contains(event.target as Node) &&
      toggleButton &&
      !toggleButton.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  if (!user || !isAdmin()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[--bg-secondary]" onClick={handleClickOutside}>
      {/* Admin Header */}
      <header className="fixed top-0 left-0 right-0 bg-[--bg-primary] border-b border-[--border-primary] z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/admin" className="flex items-center">
                <Building className="w-8 h-8 text-primary-600" />
                <span className="ml-2 text-xl font-bold text-[--text-primary]">Admin Panel</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <div className="relative">
                <button
                  ref={buttonRef}
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center px-4 py-2 text-sm text-[--text-primary] hover:bg-[--state-hover] rounded-lg"
                >
                  <User className="w-5 h-5 mr-2" />
                  <span className="text-[14px]">{user.username}</span>
                </button>

                {showUserMenu && (
                  <div
                    ref={menuRef}
                    className="absolute right-0 mt-2 w-48 bg-[--bg-primary] rounded-md shadow-lg py-1 z-50"
                  >
                    <Link
                      to="/admin/password"
                      className="block px-4 py-2 text-sm text-[--text-primary] hover:bg-[--state-hover]"
                      onClick={() => setShowUserMenu(false)}
                    >
                      Change Password
                    </Link>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        handleLogout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-[--text-primary] hover:bg-[--state-hover]"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="pt-16 flex">
        {/* Admin Sidebar */}
        <div
          id="admin-sidebar"
          className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-[--bg-primary] border-r border-[--border-primary] transition-all duration-300 z-40 ${
            isOpen ? 'w-64' : 'w-20'
          }`}
        >
          <div className="p-4">
            <button
              id="sidebar-toggle"
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between p-2 hover:bg-[--state-hover] rounded-lg"
            >
              <span className={`font-semibold text-[--text-primary] ${isOpen ? 'block' : 'hidden'}`}>
                Navigation
              </span>
              <ChevronDown className={`w-5 h-5 text-[--text-secondary] transform transition-transform ${isOpen ? 'rotate-0' : '-rotate-90'}`} />
            </button>
          </div>
          <nav className="space-y-1 px-2">
            {adminNavItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  currentPath === item.path
                    ? "bg-[--state-selected] text-[--link-default]"
                    : "text-[--text-primary] hover:bg-[--state-hover]"
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {isOpen && <span className="flex-1">{item.name}</span>}
              </Link>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className={`flex-1 transition-all duration-300 ml-20`}>
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}